<template>
	<div class="box" :class="borderCheck">
		<div class="level">
			<div class="level-left">
				<h3
					v-if="heading"
					class="title is-4 has-text-weight-bold"
					v-text="heading"
				/>
			</div>
			<div class="level-right">
				<a
					v-if="tableData && exceldownload"
					class="button is-small gtm-download-excel level-item"
					:disabled="!canDownload"
					:href="exceldownload.link"
					target="_blank"
				>
					<dc-icon icon="download" />
					<span v-text="exceldownload.label" />
				</a>
				<button
					v-if="tableData && csvdownload"
					class="button is-small gtm-download-csv level-item"
					:disabled="!canDownload"
					@click.prevent="downloadableCSV"
				>
					<dc-icon icon="download" />
					<span v-text="csvdownload.label" />
				</button>
			</div>
		</div>
		<b-table
			v-if="rows && rows.length > 0"
			:data="rows"
			:columns="columns"
			:paginated="isPaginated"
			:per-page="perPage"
			:loading="isFetching"
			:default-sort="defaultSort"
		>
			<template slot="empty">
				<section class="section">
					<div class="content has-text-grey has-text-centered">
						<p>{{ errorMessage }}</p>
					</div>
				</section>
			</template>
		</b-table>
		<span v-else> There is no data to display </span>
	</div>
</template>

<script>
import sourceMixin from "./sourceMixin";
import format from "date-fns/format";
import FileSaver from "file-saver";
import { exportDownloadableContent } from "@lab/api";

export default {
	mixins: [sourceMixin],
	data() {
		return {
			tableData: {
				data: [],
				columns: []
			}
		};
	},
	computed: {
		heading() {
			return this.data.title;
		},
		listingid() {
			return this.data.listingid;
		},
		hospitalid() {
			return this.data.hospitalid;
		},
		organisationid() {
			return this.data.organisationid;
		},
		perPage() {
			return this.data.perPage || 10;
		},
		query() {
			return this.tableData ? this.data.query : null;
		},
		columns() {
			return this.tableData ? this.tableData.columns : [];
		},
		rows() {
			return this.tableData ? this.tableData.data : [];
		},
		defaultSort() {
			return this.tableData ? this.tableData.defaultSort : "";
		},
		isPaginated() {
			return this.rows && this.rows.length > this.perPage;
		},
		csvdownload() {
			if (this.data.csvdownload) {
				let { link, label } = this.data.csvdownload;

				if (/{ListingID}/.test(link)) {
					link = link.replace("{ListingID}", this.listingid);
				}

				if (/{HospitalID}/.test(link)) {
					link = link.replace("{HospitalID}", this.hospitalid);
				}

				if (/{OrganisationID}/.test(link)) {
					link = link.replace("{OrganisationID}", this.organisationid);
				}

				if (this.query) {
					link += `?${this.query}`;
				}

				return { link, label };
			}
			return null;
		},
		exceldownload() {
			if (this.data.exceldownload) {
				let { link, label } = this.data.exceldownload;

				if (this.listingid && /{ListingID}/.test(link)) {
					link = link.replace("{ListingID}", this.listingid);
				}

				if (this.hospitalid && /{HospitalID}/.test(link)) {
					link = link.replace("{HospitalID}", this.hospitalid);
				}

				if (this.organisationid && /{OrganisationID}/.test(link)) {
					link = link.replace("{OrganisationID}", this.organisationid);
				}

				if (window.localStorage.getItem("token") && /{Token}/.test(link)) {
					link = link.replace("{Token}", window.localStorage.getItem("token"));
				}

				if (this.query) {
					// If the source already has a query string, then change the
					// separator to "&"
					let sep = link.includes("?") ? "&" : "?";
					link = `${link}${sep}${this.query}`;
				}
				if (/{BaseURL}/.test(link)) {
					link = link.replace("{BaseURL}", process.env.VUE_APP_SERVICES);
				}

				return { link, label };
			}
			return null;
		},
		canDownload() {
			return !!this.rows && this.rows.length;
		}
	},
	methods: {
		async fetchData() {
			this.isFetching = true;
			this.tableData = null;
			this.label = "Loading";

			if (!this.isValid(this.source)) {
				this.isFetching = false;
				return;
			}

			await this.fetchSource(this.source).then(
				(response) => {
					this.tableData = response;
					this.isFetching = false;
					if (!response) {
						this.label = this.errorMessage;
					}
				},
				() => {
					this.label = this.errorMessage;
					this.isFetching = false;
				}
			);
		},
		async downloadableCSV() {
			try {
				const { link } = this.csvdownload;
				// 1. call the api call for exporting org users
				const csv = await exportDownloadableContent(link, "post");

				// 2. create the filename for downloading
				const { title } = this.data;
				const date = format(new Date(), "ddMMyyyy_HHmmss");
				const filename = `${title
					.toLowerCase()
					.replace(/\s+/g, "-")}-${date}.csv`;

				const csvFile = new Blob([csv], { type: "text/csv;charset=utf-8" });

				FileSaver.saveAs(csvFile, filename);
			} catch (error) {
				// Just rethrowing error for the moment.
				// will set isFetching to be false here though
				console.log(error);
				throw error;
			}
			// console.log(context);
		}
	}
};
</script>

<style scoped>
.b-table >>> .table-wrapper {
	overflow-x: auto;
}
.b-table >>> .table {
	font-size: 0.9rem;
}
.b-table >>> .table thead th {
	border-bottom-width: 1px;
}
.b-table >>> .table td,
.b-table >>> .table th {
	padding: 0.5rem;
}
.b-table >>> .table tbody tr:last-child td {
	border-bottom-width: 1px;
}
.b-table >>> .level {
	padding-bottom: 0;
}
</style>
